import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmtDate, fmtMoney, todayISO } from '../lib/format';
import type { FollowUpItem, ProductSuggestion } from '../lib/types';
import { Button, Dialog, Empty, ErrorState, Field, Ltr, SkeletonRows, Tabs, Textarea, useConfirm, useToast } from '../components/ui';
import { IconPhone } from '../components/icons';

type Reason = 'all' | FollowUpItem['reason'];

const REASON_TEXT: Record<FollowUpItem['reason'], string> = {
  cycle: 'עבר את קצב ההזמנות הרגיל',
  dormant: 'רדום',
  products: 'מוצרים שלא הוזמנו מזמן',
};

export function whyText(f: Pick<FollowUpItem, 'reason' | 'usualEveryDays' | 'daysSinceLastOrder' | 'orders' | 'lastOrder'>) {
  if (f.reason === 'cycle') return `מזמין בדרך כלל כל ~${f.usualEveryDays} ימים · ההזמנה האחרונה לפני ${f.daysSinceLastOrder} ימים (${fmtDate(f.lastOrder)})`;
  if (f.reason === 'dormant') return f.orders === 1 ? `הזמין פעם אחת, לפני ${f.daysSinceLastOrder} ימים` : `לא הזמין ${f.daysSinceLastOrder} ימים`;
  return `מזמין בקביעות — אבל חלק מהמוצרים שהזמין בעבר לא הוזמנו מזמן`;
}

export function ProductChip({ p }: { p: ProductSuggestion }) {
  return (
    <span className="chip" title={`${p.items.join('\n')}\n\nהוזמן ${p.timesBought} פעמים · לאחרונה ${fmtDate(p.lastBought)}${p.usualEveryDays ? ` · בדרך כלל כל ~${p.usualEveryDays} ימים` : ''}`}>
      <strong>{p.label}</strong>
      <span className="muted">לא הוזמן {p.daysSince} ימים</span>
    </span>
  );
}

export function FollowUpsPage() {
  const [params, setParams] = useSearchParams();
  const reason = (params.get('reason') as Reason) || 'all';
  const focus = Number(params.get('customer')) || null;
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['followups'], queryFn: () => api<{ items: FollowUpItem[]; hidden: number; summary: Record<FollowUpItem['reason'], number> }>('/api/followups') });
  const [contacting, setContacting] = useState<FollowUpItem | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const focusRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (focus && data) focusRef.current?.scrollIntoView({ block: 'center' });
  }, [focus, data]);

  const act = async (f: FollowUpItem, action: 'snoozed' | 'dismissed', until?: string) => {
    if (action === 'dismissed' && !(await confirm({ title: `להסיר את ${f.businessName} מהמעקב?`, body: 'הלקוח לא יוצע יותר למעקב הזמנות. אפשר להחזיר אותו מתיק הלקוח.', confirmLabel: 'הסרה' }))) return;
    try {
      await api(`/api/customers/${f.id}/followup`, { body: { action, until } });
      qc.invalidateQueries({ queryKey: ['followups'] });
      qc.invalidateQueries({ queryKey: ['workplan'] });
      toast.success(action === 'snoozed' ? `נדחה עד ${fmtDate(until!)}` : 'הלקוח הוסר מהמעקב');
    } catch (e) {
      toast.error(e);
    }
  };
  const inDays = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);

  const items = (data?.items ?? []).filter((i) => reason === 'all' || i.reason === reason);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>מעקב הזמנות</h1>
          <div className="sub">לקוחות שכדאי לפנות אליהם עכשיו — לפי קצב ההזמנות שלהם ולפי מוצרים שלא הזמינו מזמן. מסודר לפי שווי הזדמנות.</div>
        </div>
      </div>

      <Tabs<Reason>
        value={reason}
        onChange={(k) => {
          const next = new URLSearchParams(params);
          if (k === 'all') next.delete('reason');
          else next.set('reason', k);
          next.delete('customer');
          setParams(next, { replace: true });
        }}
        tabs={[
          { key: 'all', label: 'הכל', count: data?.items.length },
          { key: 'cycle', label: 'עברו את הקצב הרגיל', count: data?.summary.cycle },
          { key: 'products', label: 'מוצרים להציע', count: data?.summary.products },
          { key: 'dormant', label: 'רדומים', count: data?.summary.dormant },
        ]}
      />

      {contacting ? <ContactDialog item={contacting} onClose={() => setContacting(null)} /> : null}

      {error ? (
        <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>
      ) : isLoading ? (
        <div className="panel"><table className="table"><tbody><SkeletonRows rows={6} cols={3} /></tbody></table></div>
      ) : items.length === 0 ? (
        <div className="panel"><Empty title="אין כרגע לקוחות למעקב" body="כל הלקוחות מזמינים בקצב הרגיל שלהם." /></div>
      ) : (
        <div className="stack">
          {items.map((f) => (
            <div key={f.id} ref={f.id === focus ? focusRef : undefined} className={`panel followup ${f.id === focus ? 'focused' : ''}`}>
              <div className="followup-main">
                <div className="row wrap" style={{ gap: 10 }}>
                  <Link to={`/customers/${f.id}`} className="strong" style={{ color: 'var(--ink)', fontSize: 15 }}>{f.businessName}</Link>
                  <span className={`badge ${f.reason === 'dormant' ? 'warn' : f.reason === 'cycle' ? 'info' : 'accent'}`}>{REASON_TEXT[f.reason]}</span>
                  {f.lastAction ? <span className="small muted">פעולה אחרונה: {f.lastAction.action === 'contacted' ? 'פנייה' : 'דחייה'} {fmtDate(f.lastAction.createdAt)}</span> : null}
                </div>
                <div className="small muted">{whyText(f)} · {f.orders} הזמנות · הזמנה ממוצעת {fmtMoney(f.avgOrder, { round: true })}</div>
                {f.products.length ? (
                  <div className="row wrap" style={{ gap: 6 }}>
                    <span className="small">להציע שוב:</span>
                    {f.products.map((p) => <ProductChip key={p.category} p={p} />)}
                  </div>
                ) : null}
                {f.crossSell.length ? (
                  <div className="row wrap small" style={{ gap: 6 }}>
                    <span>לא קונה אצלנו:</span>
                    {f.crossSell.map((c) => (
                      <span key={c.category} className="chip outline" title={`${c.buyersShare}% מהלקוחות קונים ${c.label}`}>{c.label}</span>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="followup-side">
                {f.phone ? (
                  <a className="btn sm" href={`tel:${f.phone}`}><IconPhone /> <Ltr>{f.phone}</Ltr></a>
                ) : null}
                {f.contactName ? <span className="small muted">{f.contactName}</span> : null}
                <div className="row wrap" style={{ gap: 6 }}>
                  <Button size="sm" variant="primary" onClick={() => setContacting(f)}>פניתי</Button>
                  <Button size="sm" onClick={() => act(f, 'snoozed', inDays(7))}>דחה שבוע</Button>
                  <Button size="sm" variant="ghost" onClick={() => act(f, 'dismissed')}>הסר</Button>
                </div>
              </div>
            </div>
          ))}
          {data && data.hidden ? <p className="small muted">{data.hidden} לקוחות מוסתרים כרגע (בוצעה פנייה לאחרונה או נדחו).</p> : null}
        </div>
      )}
    </>
  );
}

function ContactDialog({ item, onClose }: { item: FollowUpItem; onClose: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  const save = async () => {
    setBusy(true);
    try {
      await api(`/api/customers/${item.id}/followup`, { body: { action: 'contacted', note: note.trim() || undefined } });
      for (const k of ['followups', 'workplan', 'customer']) qc.invalidateQueries({ queryKey: [k] });
      toast.success('הפנייה נרשמה בתיק הלקוח');
      onClose();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`פנייה ל${item.businessName}`}
      footer={
        <>
          <Button variant="primary" onClick={save} loading={busy}>שמירה</Button>
          <Button onClick={onClose}>ביטול</Button>
        </>
      }
    >
      <div className="stack">
        <Field label="מה סוכם? (לא חובה)">
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="למשל: יזמינו נייר וקפה בשבוע הבא" autoFocus />
        </Field>
        <p className="small muted">הלקוח יוסתר מהרשימה עד שיזמין שוב, או לתקופה שנקבעה בהגדרות. הפנייה נרשמת בציר הפעילות של הלקוח ({todayISO().split('-').reverse().join('/')}).</p>
      </div>
    </Dialog>
  );
}
